import { __assign, __extends, __rest } from "tslib";
import * as React from "react";
import { Animated, TextInput as NativeTextInput, Platform } from "react-native";
import PnmacTextInputOutlined from "./PnmacTextInputOutlined";
import PnmacTextInputFlat from "./PnmacTextInputFlat";
import { LineWeightSharp } from "@material-ui/icons";
var BLUR_ANIMATION_DURATION = 180;
var FOCUS_ANIMATION_DURATION = 150;
/**
 * A component to allow users to input text.
 *
 * ## Usage
 * ```js
 * import * as React from 'react';
 * import { PnmacTextInput } from 'pnmac-universal-components';
 *
 * export default class MyComponent extends React.Component {
 *   state = {
 *     text: ''
 *   };
 *
 *   render(){
 *     return (
 *       <PnmacTextInput
 *         label='Email'
 *         value={this.state.text}
 *         onChangeText={text => this.setState({ text })}
 *       />
 *     );
 *   }
 * }
 * ```
 *
 */
var PnmacTextInput = /** @class */function (_super) {
  __extends(PnmacTextInput, _super);
  function PnmacTextInput() {
    var _this = this;
    var _a, _b;
    _this = _super.apply(this, arguments) || this;
    _this.state = {
      labeled: new Animated.Value((_this.props.value !== undefined ? _this.props.value : _this.props.defaultValue) ? 0 : 1),
      error: new Animated.Value(_this.props.error ? 1 : 0),
      focused: false,
      placeholder: ((_a = _this.props) === null || _a === void 0 ? void 0 : _a.placeholder) ? (_b = _this.props) === null || _b === void 0 ? void 0 : _b.placeholder : "",
      value: _this.props.value !== undefined ? _this.props.value : _this.props.defaultValue,
      labelLayout: {
        measured: false,
        width: 0,
        height: 0
      }
    };
    _this.showPlaceholder = function () {
      if (_this.timer) {
        clearTimeout(_this.timer);
      }
      _this.timer = setTimeout(function () {
        return _this.setState({
          placeholder: _this.props.placeholder
        });
      }, 50);
    };
    _this.hidePlaceholder = function () {
      return _this.setState({
        placeholder: ""
      });
    };
    _this.showError = function () {
      Animated.timing(_this.state.error, {
        toValue: 1,
        duration: FOCUS_ANIMATION_DURATION * 1.0,
        useNativeDriver: Platform.select({
          ios: false,
          default: true
        })
      }).start(_this.showPlaceholder);
    };
    _this.hideError = function () {
      Animated.timing(_this.state.error, {
        toValue: 0,
        duration: BLUR_ANIMATION_DURATION * 1.0,
        useNativeDriver: Platform.select({
          ios: false,
          default: true
        })
      }).start();
    };
    _this.restoreLabel = function () {
      Animated.timing(_this.state.labeled, {
        toValue: 1,
        duration: FOCUS_ANIMATION_DURATION * 1.0,
        useNativeDriver: Platform.select({
          ios: false,
          default: true
        })
      }).start();
    };
    _this.minimizeLabel = function () {
      Animated.timing(_this.state.labeled, {
        toValue: 0,
        duration: BLUR_ANIMATION_DURATION * 1.0,
        useNativeDriver: Platform.select({
          ios: false,
          default: true
        })
      }).start();
    };
    _this.handleFocus = function (args) {
      if (_this.props.disabled || !_this.props.editable) {
        return;
      }
      _this.setState({
        focused: true
      });
      if (_this.props.onFocus) {
        _this.props.onFocus(args);
      }
    };
    _this.handleCopy = function (args) {
      if (_this.props.disabled || !_this.props.editable) {
        return;
      }
      if (_this.props.onCopy) {
        _this.props.onCopy(args);
      }
    };
    _this.handlePaste = function (args) {
      if (_this.props.disabled || !_this.props.editable) {
        return;
      }
      if (_this.props.onPaste) {
        _this.props.onPaste(args);
      }
    };
    _this.handleCut = function (args) {
      if (_this.props.disabled || !_this.props.editable) {
        return;
      }
      if (_this.props.onPaste) {
        _this.props.onCut(args);
      }
    };
    _this.handleBlur = function (args) {
      if (_this.props.disabled || !_this.props.editable) {
        return;
      }
      _this.setState({
        focused: false
      });
      if (_this.props.onBlur) {
        _this.props.onBlur(args);
      }
    };
    _this.handleChangeText = function (value) {
      if (!_this.props.editable) {
        return;
      }
      _this.setState({
        value: value
      });
      _this.props.onChangeText && _this.props.onChangeText(value);
    };
    _this.handleLayoutAnimatedText = function (e) {
      _this.setState({
        labelLayout: {
          width: e.nativeEvent.layout.width,
          height: e.nativeEvent.layout.height,
          measured: true
        }
      });
    };
    return _this;
  }
  PnmacTextInput.getDerivedStateFromProps = function (nextProps, prevState) {
    return {
      value: typeof nextProps.value !== "undefined" ? nextProps.value : prevState.value
    };
  };
  PnmacTextInput.prototype.componentDidUpdate = function (prevProps, prevState) {
    if (prevState.focused !== this.state.focused || prevState.value !== this.state.value || prevState.labelLayout !== this.state.labelLayout) {
      if (this.state.value || this.state.focused) {
        this.minimizeLabel();
      } else {
        this.restoreLabel();
      }
    }
    if (prevProps.error !== this.props.error) {
      if (this.props.error) {
        this.showError();
      } else {
        this.hideError();
      }
    }
  };
  PnmacTextInput.prototype.componentWillUnmount = function () {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  };
  PnmacTextInput.prototype.setNativeProps = function (args) {
    return this.root && this.root.setNativeProps(args);
  };
  PnmacTextInput.prototype.isFocused = function () {
    return this.root && this.root.isFocused();
  };
  PnmacTextInput.prototype.clear = function () {
    return this.root && this.root.clear();
  };
  PnmacTextInput.prototype.focus = function () {
    return this.root && this.root.focus();
  };
  PnmacTextInput.prototype.blur = function () {
    return this.root && this.root.blur();
  };
  PnmacTextInput.prototype.render = function () {
    var _this = this;
    var _a = this.props,
      mode = _a.mode,
      accessibilityLabel = _a.accessibilityLabel,
      rest = __rest(_a, ["mode", "accessibilityLabel"]);
    return mode === "outlined" ? (/*#__PURE__*/React.createElement(PnmacTextInputOutlined, __assign({}, rest, {
      value: this.state.value,
      parentState: this.state,
      innerRef: function (ref) {
        _this.root = ref;
      },
      onCopy: this.handleCopy,
      onPaste: this.handlePaste,
      onCut: this.handleCut,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onChangeText: this.handleChangeText,
      onLayoutAnimatedText: this.handleLayoutAnimatedText,
      testID: rest.name ? "textInput_".concat(rest.name) : 'textInput',
      inputName: rest.name || ''
    }))) : (/*#__PURE__*/React.createElement(PnmacTextInputFlat, __assign({}, rest, {
      accessibilityLabel: accessibilityLabel,
      value: this.state.value,
      parentState: this.state,
      innerRef: function (ref) {
        _this.root = ref;
      },
      onCopy: this.handleCopy,
      onPaste: this.handlePaste,
      onCut: this.handleCut,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onChangeText: this.handleChangeText,
      onLayoutAnimatedText: this.handleLayoutAnimatedText,
      testID: rest.name ? "textInput_".concat(rest.name) : 'textInput'
    })));
  };
  PnmacTextInput.defaultProps = {
    mode: "flat",
    dense: false,
    disabled: false,
    error: false,
    isValid: false,
    multiline: false,
    editable: true,
    render: function (props) {
      return /*#__PURE__*/React.createElement(NativeTextInput, __assign({
        "aria-label": "input",
        accessibilityLabel: props.accessibilityLabel
      }, props));
    }
  };
  return PnmacTextInput;
}(React.Component);
export default PnmacTextInput;